import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cx } from './cx';
import {
  MODAL_BACKDROP_INNER_CLASS,
  MODAL_MAX_H,
  MODAL_PANEL_CLASS,
  MODAL_VIEWPORT_MAX,
  modalWidthStyle,
  type ModalWidthVar,
} from '@/styles/modalTokens';
import { useModalMotion } from '@/hooks/useModalMotion';
import { wfSectionTitleStyle } from '@/styles/typography';

export interface UiModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Prevent closing via backdrop / escape (e.g. while processing) */
  locked?: boolean;
  /** When true, backdrop click dismisses. Default false — page under stays blocked. */
  closeOnOverlayClick?: boolean;
  /** Vertically center on all breakpoints (default: bottom sheet on mobile). */
  centered?: boolean;
  /**
   * Size panel to content — body does not scroll.
   * Use for short sheets (amount / confirm). Tall lists should scroll inside children.
   */
  fitContent?: boolean;
  className?: string;
}

const SIZE_VARS: Record<NonNullable<UiModalProps['size']>, ModalWidthVar> = {
  sm: '--wf-mdl-w',
  md: '--wf-mdl-payout-w',
  lg: '--wf-mdl-game-w',
};

/** Cosmos modal: opaque panel over dimmed starfield backdrop. */
export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  locked,
  closeOnOverlayClick = false,
  centered = false,
  fitContent = false,
  className,
}: UiModalProps) {
  const { render, exiting, backdropClass, panelClass } = useModalMotion(open);

  useEffect(() => {
    if (!render) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !locked && !exiting) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [render, locked, exiting, onClose]);

  if (!render) return null;

  const pad = 'var(--wf-mdl-pad)';
  const canDismissOverlay = !locked && !exiting && closeOnOverlayClick;

  return createPortal(
    <div
      className={cx(
        'fixed inset-0 z-[var(--wf-z-modal-nested)] flex justify-center',
        centered ? 'items-center p-4' : 'items-end sm:items-center p-0 sm:p-4'
      )}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cx(MODAL_BACKDROP_INNER_CLASS, backdropClass)}
        onClick={canDismissOverlay ? onClose : undefined}
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        aria-hidden
      />
      <div
        className={cx(
          MODAL_PANEL_CLASS,
          centered
            ? 'rounded-[var(--wf-mdl-radius)]'
            : 'rounded-t-[var(--wf-mdl-radius)] sm:rounded-[var(--wf-mdl-radius)]',
          panelClass,
          className
        )}
        style={{
          ...modalWidthStyle(SIZE_VARS[size]),
          maxHeight: MODAL_MAX_H,
          maxWidth: MODAL_VIEWPORT_MAX,
          borderRadius: 'var(--wf-mdl-radius)',
          ...(fitContent ? { height: 'auto' } : null),
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between gap-3 shrink-0"
          style={{
            paddingTop: pad,
            paddingBottom: 'calc(10px * var(--proto-scale))',
            paddingInline: pad,
          }}
        >
          {title ? (
            <h2
              className="font-display font-bold text-ink min-w-0 flex-1"
              style={wfSectionTitleStyle}
            >
              {title}
            </h2>
          ) : (
            <span />
          )}
          {!locked && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="Close"
              className="inline-flex items-center justify-center min-h-[var(--wf-control-h,40px)] min-w-[var(--wf-control-h,40px)] rounded-control-sm border border-border-ice bg-white/5 text-ice hover:bg-white/10 hover:text-ink transition-colors focus-visible:outline-none focus-visible:shadow-focus-ring shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div
          className={cx(
            fitContent
              ? 'shrink-0 overflow-visible'
              : 'flex-1 min-h-0 cosmos-scroll-y overscroll-contain'
          )}
          style={{ paddingInline: pad, paddingBottom: footer ? undefined : pad }}
        >
          {children}
        </div>
        {footer && (
          <div
            className="shrink-0 border-t border-border-ice flex flex-wrap gap-2 justify-stretch sm:justify-end"
            style={{ padding: pad }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
